import type { Root, RootContent } from 'mdast'
import type { ContainerDirective, LeafDirective, TextDirective } from 'mdast-util-directive'

type Directive = ContainerDirective | LeafDirective | TextDirective
type ParentNode = { children: RootContent[]; data?: { buttonRow?: boolean } }

export const DIRECTIVE_TAG = 'news-directive'

export type DirectiveProps = {
  name: string
  kind: Directive['type']
  attributes: Record<string, string>
}

const isDirective = (node: RootContent): node is Directive =>
  node.type === 'containerDirective' || node.type === 'leafDirective' || node.type === 'textDirective'

const isButton = (node: RootContent) => node.type === 'leafDirective' && node.name === 'button'

/** Each directive's props travel as one JSON property, so they reach the component untouched by hast's property mapping. */
export const readDirectiveProps = (encoded: unknown): DirectiveProps => JSON.parse(String(encoded)) as DirectiveProps

const tagDirective = (node: Directive) => {
  if (node.type === 'containerDirective') {
    node.children = node.children.filter((child) => !(child.type === 'paragraph' && child.data?.directiveLabel))
  }
  const attributes = Object.fromEntries(Object.entries(node.attributes ?? {}).map(([k, v]) => [k, v ?? '']))
  const props: DirectiveProps = { name: node.name, kind: node.type, attributes }
  node.data = { ...node.data, hName: DIRECTIVE_TAG, hProperties: { directive: JSON.stringify(props) } }
}

/** Consecutive `::button`s share one row instead of stacking as separate blocks. */
const groupButtons = (parent: ParentNode) => {
  if (parent.data?.buttonRow) {
    return
  }
  const grouped: RootContent[] = []
  for (const child of parent.children) {
    const previous = grouped.at(-1) as ParentNode | undefined
    if (!isButton(child)) {
      grouped.push(child)
    } else if (previous?.data?.buttonRow) {
      previous.children.push(child)
    } else {
      grouped.push({
        type: 'paragraph',
        children: [child],
        data: { buttonRow: true, hName: 'div', hProperties: { className: ['not-prose', 'my-5', 'flex', 'flex-wrap', 'gap-3'] } }
      } as unknown as RootContent)
    }
  }
  parent.children = grouped
}

const transform = (parent: ParentNode) => {
  groupButtons(parent)
  for (const child of parent.children) {
    if (isDirective(child)) {
      tagDirective(child)
    }
    if ('children' in child) {
      transform(child as ParentNode)
    }
  }
}

export const remarkNewsDirectives = () => (tree: Root) => {
  transform(tree as ParentNode)
}
